import React from "react";
import { rubricColor, dimensionShort, dimensionLabel } from "../lib/scoring";

const DIMENSIONS = [
  "optimism",
  "emotional_regulation",
  "ownership",
  "flexibility",
  "social_support",
  "purpose",
  "grit",
];

const METRICS = [
  { key: "mean", label: "Mean", format: (d) => (d.mean != null ? d.mean.toFixed(2) : "—") },
  {
    key: "top_box",
    label: "Top Box",
    format: (d) => (d.tb_pct != null ? Math.round(d.tb_pct) + "%" : "—"),
  },
  { key: "rubric", label: "Rubric", format: (d) => d.rubric_level || "—" },
];

export default function Heatmap({ heatmap, metric, onMetricChange, onCellClick }) {
  if (!heatmap) return null;
  const segments = heatmap.segments || [];
  const m = METRICS.find((x) => x.key === metric) || METRICS[0];

  return (
    <div data-testid="heatmap">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-1">Heatmap</div>
          <div className="text-sm text-gray-700">Segments × dimensions. Cells with n &lt; 5 are suppressed.</div>
        </div>
        <div role="tablist" className="flex gap-1 bg-cream border border-gray-100 p-1" data-testid="metric-toggle">
          {METRICS.map((opt) => (
            <button
              key={opt.key}
              role="tab"
              aria-selected={metric === opt.key}
              onClick={() => onMetricChange(opt.key)}
              className="px-4 py-2 text-xs"
              style={{
                fontFamily: "Oswald",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                background: metric === opt.key ? "#1A1A1A" : "transparent",
                color: metric === opt.key ? "#FBFAF7" : "#555555",
                transition: "all 100ms ease",
              }}
              data-testid={`metric-${opt.key}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto -mx-2 px-2">
        <table className="w-full border-collapse" data-testid="heatmap-table">
          <thead>
            <tr>
              <th className="text-left pr-4 pb-3 align-bottom" style={{ minWidth: "200px" }}>
                <div className="eyebrow text-gray-500">Segment</div>
              </th>
              {DIMENSIONS.map((d) => (
                <th key={d} className="px-2 pb-3 align-bottom" style={{ minWidth: "78px" }}>
                  <div
                    className="text-xs text-center"
                    style={{ fontFamily: "Oswald", fontWeight: 600, letterSpacing: "0.1em", color: "#555555" }}
                    title={dimensionLabel(d)}
                  >
                    {dimensionShort(d)}
                  </div>
                </th>
              ))}
              <th className="pl-3 pb-3 align-bottom">
                <div className="eyebrow text-gray-500">n</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {segments.map((seg) => (
              <tr key={`${seg.segment_key}:${seg.segment_value}`} className="border-t border-gray-100">
                <td className="pr-4 py-3">
                  <div className="text-sm" style={{ fontFamily: "Inter", fontWeight: 500, color: "#1A1A1A" }}>
                    {seg.segment_value}
                  </div>
                  {seg.segment_key !== "_all" && (
                    <div className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}>
                      {seg.segment_key}
                    </div>
                  )}
                </td>
                {DIMENSIONS.map((dKey) => {
                  if (seg.suppressed) {
                    return <SuppressedCell key={dKey} n={seg.n} testId={`cell-${seg.segment_value}-${dKey}-suppressed`} />;
                  }
                  const dim = (seg.dimensions || []).find((x) => x.dimension === dKey);
                  if (!dim) return <td key={dKey} className="px-2 py-2"><div className="bg-gray-100 h-12" /></td>;
                  const c = rubricColor(dim.rubric_level);
                  return (
                    <td key={dKey} className="px-1 py-1">
                      <button
                        onClick={() => onCellClick && onCellClick(seg, dim)}
                        className="w-full block text-center transition-colors duration-100"
                        style={{
                          background: c.bg,
                          color: c.fg,
                          padding: "12px 6px",
                          border: `1px solid ${c.solid}33`,
                        }}
                        data-testid={`cell-${seg.segment_value}-${dKey}`}
                        title={`${dimensionLabel(dKey)}: ${dim.rubric_level} · mean ${dim.mean.toFixed(2)} · top box ${Math.round(dim.tb_pct)}%`}
                      >
                        <div className="text-base" style={{ fontFamily: "Oswald", fontWeight: 700, lineHeight: 1 }}>
                          {m.format(dim)}
                        </div>
                      </button>
                    </td>
                  );
                })}
                <td className="pl-3 py-3 text-right text-sm text-gray-700" style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}>
                  {seg.n}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Legend />
    </div>
  );
}

function SuppressedCell({ n, testId }) {
  return (
    <td className="px-1 py-1" data-testid={testId}>
      <div
        className="w-full text-center text-xs py-3"
        style={{
          background: "repeating-linear-gradient(45deg, #F5F5F5, #F5F5F5 4px, #FBFAF7 4px, #FBFAF7 8px)",
          color: "#6F6F6F",
          fontFamily: "Oswald",
          letterSpacing: "0.1em",
          border: "1px solid #EEEEEE",
          cursor: "not-allowed",
        }}
        title={`Suppressed — only ${n} respondent${n === 1 ? "" : "s"} in this segment (need 5+)`}
        aria-disabled="true"
      >
        n&lt;5
      </div>
    </td>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-6 mt-6 text-xs flex-wrap" data-testid="heatmap-legend">
      {Object.entries({
        Strong: "#059669",
        Developing: "#2563EB",
        Emerging: "#D97706",
        "At Risk": "#DC2626",
      }).map(([level, color]) => (
        <div key={level} className="flex items-center gap-2">
          <span className="block" style={{ width: "12px", height: "12px", background: color }} />
          <span className="text-gray-700" style={{ fontFamily: "Oswald", letterSpacing: "0.1em", textTransform: "uppercase" }}>
            {level}
          </span>
        </div>
      ))}
      <div className="flex items-center gap-2 ml-auto">
        <span
          className="block"
          style={{
            width: "12px",
            height: "12px",
            background: "repeating-linear-gradient(45deg, #F5F5F5, #F5F5F5 2px, #FBFAF7 2px, #FBFAF7 4px)",
          }}
        />
        <span className="text-gray-500" style={{ fontFamily: "Oswald", letterSpacing: "0.1em", textTransform: "uppercase" }}>
          Suppressed (n&lt;5)
        </span>
      </div>
    </div>
  );
}
